// src/store/pos/posSessionOfflinePersistence.js

import indexedDb
  from "@/platform/storage/indexedDb";

// ============================================================
// CONSTANTS
// ============================================================

const STORE_NAME =
  "posSessions";

// ============================================================
// CLIENT SESSION ID
//
// Offline sessions need a durable client-generated identity
// before the ASP.NET backend is available.
//
// When connectivity returns:
//
// clientSessionId
//      ↓
// POS_SESSION_OPEN
//      ↓
// backend creates session
//      ↓
// serverSessionId
// ============================================================

function createClientSessionId() {
  if (
    typeof crypto !==
      "undefined" &&
    typeof crypto.randomUUID ===
      "function"
  ) {
    return crypto.randomUUID();
  }

  return [
    "pos-session",
    Date.now(),
    Math.random()
      .toString(16)
      .slice(2)
  ].join("-");
}

// ============================================================
// TERMINAL ID
// ============================================================

function getTerminalId(
  terminal
) {
  return (
    terminal?.id
    ??
    terminal?.terminalId
    ??
    null
  );
}

// ============================================================
// CREATE OFFLINE SESSION
// ============================================================

async function createOfflineSession({
  terminal,
  openingCashAmount = 0,
  currencyCode = "CAD",
  notes = null,
  userId = null
}) {
  if (!terminal) {
    throw new Error(
      "[POS] Terminal is required to create an offline session."
    );
  }

  const terminalId =
    getTerminalId(
      terminal
    );

  if (!terminalId) {
    throw new Error(
      "[POS] Terminal ID is required to create an offline session."
    );
  }

  // ==========================================================
  // EXISTING OPEN LOCAL SESSION
  //
  // Prevent accidentally creating multiple local sessions for
  // the same terminal.
  // ==========================================================

  const existing =
    await getOpenByTerminalId(
      terminalId
    );

  if (existing) {
    return existing;
  }

  const clientSessionId =
    createClientSessionId();

  const now =
    new Date()
      .toISOString();

  const session = {
    // ========================================================
    // LOCAL SESSION IDENTITY
    // ========================================================

    clientSessionId,

    /*
     * Existing POS components determine an active session
     * using:
     *
     * session.id
     * ??
     * session.sessionId
     *
     * Therefore expose clientSessionId through both while
     * offline.
     */
    id:
      clientSessionId,

    sessionId:
      clientSessionId,

    /*
     * Filled after synchronization with ASP.NET.
     */
    serverSessionId:
      null,

    // ========================================================
    // TERMINAL
    // ========================================================

    terminalId:
      String(
        terminalId
      ),

    terminalCode:
      terminal.terminalCode
      ??
      terminal.code
      ??
      "",

    terminalName:
      terminal.terminalName
      ??
      terminal.name
      ??
      "",

    // ========================================================
    // LOCATION
    // ========================================================

    locationId:
      terminal.terminalLocationId
      ??
      terminal.locationId
      ??
      null,

    terminalLocationId:
      terminal.terminalLocationId
      ??
      terminal.locationId
      ??
      null,

    terminalLocationCode:
      terminal.terminalLocationCode
      ??
      terminal.locationCode
      ??
      "",

    terminalLocationName:
      terminal.terminalLocationName
      ??
      terminal.locationName
      ??
      "",

    // ========================================================
    // CASHIER
    // ========================================================

    userId:
      userId
      ??
      null,

    openedBy:
      userId
      ??
      null,

    // ========================================================
    // OPENING
    // ========================================================

    openingCashAmount:
      normalizeMoney(
        openingCashAmount
      ),

    currencyCode:
      normalizeCurrencyCode(currencyCode),

    notes:
      notes
      ??
      null,

    openedAtUtc:
      now,

    // ========================================================
    // CLOSING
    // ========================================================

    closingCashAmount:
      null,

    closingNotes:
      null,

    closedAtUtc:
      null,

    // ========================================================
    // SESSION STATUS
    // ========================================================

    status:
      "OPEN",

    // ========================================================
    // OFFLINE
    // ========================================================

    wasCreatedOffline:
      true,

    // ========================================================
    // SYNC
    //
    // PENDING
    // QUEUED
    // SYNCING
    // SYNCED
    // FAILED
    // ========================================================

    syncStatus:
      "PENDING",

    syncQueueId:
      null,

    syncError:
      null,

    // ========================================================
    // TIMESTAMPS
    // ========================================================

    createdAtUtc:
      now,

    updatedAtUtc:
      now
  };

  await indexedDb.put(
    STORE_NAME,
    session
  );

  return normalizeSession(
    session
  );
}

// ============================================================
// SAVE SESSION
// ============================================================

async function saveSession(
  session
) {
  if (
    !session?.clientSessionId
  ) {
    return null;
  }

  const updated = {
    ...session,

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// GET SESSION BY CLIENT SESSION ID
// ============================================================

async function getSession(
  clientSessionId
) {
  if (!clientSessionId) {
    return null;
  }

  const session =
    await indexedDb.get(
      STORE_NAME,
      clientSessionId
    );

  return session
    ? normalizeSession(
        session
      )
    : null;
}

// ============================================================
// GET OPEN SESSION BY TERMINAL
// ============================================================

async function getOpenByTerminalId(
  terminalId
) {
  if (!terminalId) {
    return null;
  }

  const sessions =
    await indexedDb.getAllByIndex(
      STORE_NAME,
      "terminalId",
      String(
        terminalId
      )
    );

  if (
    !Array.isArray(
      sessions
    )
  ) {
    return null;
  }

  const openSession =
    sessions.find(
      session =>
        normalizeStatus(
          session?.status
        ) ===
        "OPEN"
    )
    ??
    null;

  return openSession
    ? normalizeSession(
        openSession
      )
    : null;
}

// ============================================================
// GET BY SERVER SESSION ID
// ============================================================

async function getByServerSessionId(
  serverSessionId
) {
  if (!serverSessionId) {
    return null;
  }

  const sessions =
    await indexedDb.getAllByIndex(
      STORE_NAME,
      "serverSessionId",
      String(
        serverSessionId
      )
    );

  const session =
    sessions?.[0]
    ??
    null;

  return session
    ? normalizeSession(
        session
      )
    : null;
}

// ============================================================
// GET ALL OPEN SESSIONS
// ============================================================

async function getOpenSessions() {
  const sessions =
    await indexedDb.getAllByIndex(
      STORE_NAME,
      "status",
      "OPEN"
    );

  return Array.isArray(
    sessions
  )
    ? sessions.map(
        normalizeSession
      )
    : [];
}

// ============================================================
// GET ALL PENDING SYNC
// ============================================================

async function getPendingSyncSessions() {
  const all =
    await indexedDb.getAll(
      STORE_NAME
    );

  return (
    Array.isArray(
      all
    )
      ? all
          .filter(
            session =>
              [
                "PENDING",
                "QUEUED",
                "FAILED"
              ].includes(
                normalizeStatus(
                  session
                    ?.syncStatus
                )
              )
          )
          .map(
            normalizeSession
          )
      : []
  );
}

// ============================================================
// MARK QUEUED
// ============================================================

async function markQueued(
  session,
  queueId = null
) {
  if (
    !session?.clientSessionId
  ) {
    return null;
  }

  const updated = {
    ...session,

    syncStatus:
      "QUEUED",

    syncQueueId:
      queueId
      ??
      session.syncQueueId
      ??
      null,

    syncError:
      null,

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// MARK SYNCING
// ============================================================

async function markSyncing(
  session
) {
  if (
    !session?.clientSessionId
  ) {
    return null;
  }

  const updated = {
    ...session,

    syncStatus:
      "SYNCING",

    syncError:
      null,

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// MARK SYNCHRONIZED
//
// Called after POS_SESSION_OPEN has successfully created or
// resolved the authoritative backend session.
// ============================================================

async function markSynchronized(
  session,
  serverSession
) {
  if (
    !session?.clientSessionId
  ) {
    return null;
  }

  const serverSessionId =
    serverSession?.id
    ??
    serverSession?.sessionId
    ??
    serverSession?.serverSessionId
    ??
    (
      typeof serverSession ===
        "string"
        ? serverSession
        : null
    );

  if (!serverSessionId) {
    throw new Error(
      "[POS] Server session ID is required to synchronize the local POS session."
    );
  }

  const updated = {
    ...session,

    serverSessionId:
      String(
        serverSessionId
      ),

    /*
     * Keep id/sessionId as clientSessionId locally.
     *
     * This avoids changing the local transaction relationship
     * while offline data is being reconciled.
     */
    id:
      session.clientSessionId,

    sessionId:
      session.clientSessionId,

    currencyCode:
      normalizeCurrencyCode(
        serverSession?.currencyCode
        ?? serverSession?.currency
        ?? session.currencyCode
      ),

    serverTerminalId:
      serverSession?.terminalId
      ??
      session.serverTerminalId
      ??
      session.terminalId,

    syncStatus:
      "SYNCED",

    syncError:
      null,

    synchronizedAtUtc:
      new Date()
        .toISOString(),

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// MARK FAILED
// ============================================================

async function markFailed(
  session,
  error
) {
  if (
    !session?.clientSessionId
  ) {
    return null;
  }

  const updated = {
    ...session,

    syncStatus:
      "FAILED",

    syncError:
      getErrorText(
        error
      ),

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// RESET FAILED SESSION FOR RETRY
// ============================================================

async function resetForRetry(
  clientSessionId
) {
  const session =
    await getSession(
      clientSessionId
    );

  if (!session) {
    return null;
  }

  const updated = {
    ...session,

    syncStatus:
      "QUEUED",

    syncError:
      null,

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// CLOSE LOCAL SESSION
//
// This does not delete the session.
//
// It preserves reconciliation information because the backend
// may still need:
//
// POS_SESSION_OPEN
//      ↓
// POS_SALE
//      ↓
// POS_SESSION_CLOSE
// ============================================================

async function closeLocalSession(
  clientSessionId,
  {
    closingCashAmount = 0,
    notes = null
  } = {}
) {
  if (!clientSessionId) {
    return null;
  }

  const session =
    await getSession(
      clientSessionId
    );

  if (!session) {
    return null;
  }

  const now =
    new Date()
      .toISOString();

  const updated = {
    ...session,

    status:
      "CLOSED",

    closingCashAmount:
      normalizeMoney(
        closingCashAmount
      ),

    closingNotes:
      notes
      ??
      null,

    closedAtUtc:
      now,

    /*
     * If this offline session has not yet synchronized, closing
     * it locally does not make it synchronized.
     */
    syncStatus:
      session.syncStatus
      ??
      "PENDING",

    updatedAtUtc:
      now
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// REOPEN LOCAL SESSION
//
// Intended for recovery only.
// ============================================================

async function reopenLocalSession(
  clientSessionId
) {
  const session =
    await getSession(
      clientSessionId
    );

  if (!session) {
    return null;
  }

  const updated = {
    ...session,

    status:
      "OPEN",

    closingCashAmount:
      null,

    closingNotes:
      null,

    closedAtUtc:
      null,

    updatedAtUtc:
      new Date()
        .toISOString()
  };

  await indexedDb.put(
    STORE_NAME,
    updated
  );

  return normalizeSession(
    updated
  );
}

// ============================================================
// DELETE SESSION
//
// Delete only after all dependent offline jobs have been
// successfully synchronized and no transaction still requires
// this local session mapping.
// ============================================================

async function deleteSession(
  clientSessionId
) {
  if (!clientSessionId) {
    return false;
  }

  await indexedDb.delete(
    STORE_NAME,
    clientSessionId
  );

  return true;
}

// ============================================================
// CLEAR ALL
//
// Development/admin cleanup only.
// ============================================================

async function clearAll() {
  await indexedDb.clear(
    STORE_NAME
  );

  return true;
}

// ============================================================
// NORMALIZE SESSION
// ============================================================

function normalizeSession(
  session
) {
  if (!session) {
    return null;
  }

  const clientSessionId =
    session.clientSessionId
    ??
    session.id
    ??
    session.sessionId
    ??
    null;

  return {
    ...session,

    clientSessionId,

    id:
      clientSessionId,

    sessionId:
      clientSessionId,

    terminalId:
      session.terminalId !==
        null &&
      session.terminalId !==
        undefined
        ? String(
            session.terminalId
          )
        : null,

    serverSessionId:
      session.serverSessionId !==
        null &&
      session.serverSessionId !==
        undefined
        ? String(
            session.serverSessionId
          )
        : null,

    currencyCode:
      normalizeCurrencyCode(
        session.currencyCode
        ?? session.currency
      ),

    openingCashAmount:
      normalizeMoney(
        session.openingCashAmount
      ),

    closingCashAmount:
      session.closingCashAmount ===
        null ||
      session.closingCashAmount ===
        undefined
        ? null
        : normalizeMoney(
            session.closingCashAmount
          ),

    status:
      normalizeStatus(
        session.status
        ??
        "OPEN"
      ),

    syncStatus:
      normalizeStatus(
        session.syncStatus
        ??
        "PENDING"
      ),

    wasCreatedOffline:
      session.wasCreatedOffline !==
      false
  };
}

// ============================================================
// NORMALIZE CURRENCY
// Legacy records without a currency are treated as CAD.
// ============================================================

function normalizeCurrencyCode(value) {
  const code = String(value ?? "CAD")
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{3}$/.test(code)) {
    throw new Error("[POS] A valid ISO currency code is required.");
  }

  return code;
}

// ============================================================
// NORMALIZE STATUS
// ============================================================

function normalizeStatus(
  value
) {
  return String(
    value
    ??
    ""
  )
    .trim()
    .toUpperCase();
}

// ============================================================
// NORMALIZE MONEY
// ============================================================

function normalizeMoney(
  value
) {
  const numeric =
    Number(
      value
      ??
      0
    );

  if (
    !Number.isFinite(
      numeric
    )
  ) {
    return 0;
  }

  return Math.round(
    (
      numeric +
      Number.EPSILON
    ) *
    100
  ) /
  100;
}

// ============================================================
// ERROR TEXT
// ============================================================

function getErrorText(
  error
) {
  const data =
    error?.response?.data;

  if (
    typeof data ===
    "string"
  ) {
    return data;
  }

  return (
    data?.message
    ??
    data?.detail
    ??
    data?.title
    ??
    error?.message
    ??
    String(
      error
      ??
      "Unknown synchronization error."
    )
  );
}

// ============================================================
// EXPORT
// ============================================================

export default {
  createClientSessionId,

  createOfflineSession,

  saveSession,

  getSession,
  getOpenByTerminalId,
  getByServerSessionId,
  getOpenSessions,
  getPendingSyncSessions,

  markQueued,
  markSyncing,
  markSynchronized,
  markFailed,

  resetForRetry,

  closeLocalSession,
  reopenLocalSession,

  deleteSession,

  clearAll
};